let petLayer: HTMLElement | null = null;

// One layer for every pet add-on, so pets from different add-ons can overlap
// each other in depth order.
export function getPetLayer(): HTMLElement {
  if (!petLayer) {
    petLayer = document.createElement("div");
    petLayer.className = "pet-layer";
    petLayer.setAttribute("aria-hidden", "true");
    document.body.append(petLayer);
    observeCredits(petLayer);
  }

  return petLayer;
}

// Coordinates are relative to the space above the credits, including toys.
function observeCredits(layer: HTMLElement): void {
  const update = (): void => {
    let bottom = 0;
    document.querySelectorAll("#credits, #creditsMoreList").forEach((element) => {
      const rect = element.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        bottom = Math.max(bottom, window.innerHeight - rect.top);
      }
    });
    layer.style.bottom = `${Math.ceil(bottom)}px`;
  };
  const observer = new ResizeObserver(update);
  const credits = document.getElementById("credits");
  const observe = (): void => {
    if (credits) {
      observer.observe(credits);
      credits.querySelectorAll("ul").forEach((list) => observer.observe(list));
    }
    update();
  };
  if (credits) {
    new MutationObserver(observe).observe(credits, {
      childList: true,
      attributes: true,
      attributeFilter: ["class", "hidden"],
    });
  }
  window.addEventListener("resize", update);
  observe();
}

export const defaultPetBottomRange: [number, number] = [0, 120];

export function getPetBounds(
  frameSize: number,
  padding: number,
): [number, number] {
  const width = window.innerWidth;
  const margin = Math.min(padding, Math.max(12, width * 0.04));
  const min = Math.min(width / 2, margin + frameSize / 2);
  return [min, Math.max(min, width - min)];
}

export function getPetBottomRange(
  range: [number, number],
  frameSize: number,
): [number, number] {
  const ceiling = Math.max(0, getPetLayer().clientHeight - frameSize - 24);
  const min = Math.min(range[0], ceiling);
  return [min, Math.min(Math.max(min, range[1]), ceiling)];
}

// Pets lower on the screen are closer to the viewer and draw on top.
export function getPetDepth(bottom: number): string {
  return String(Math.max(0, 10_000 - Math.round(bottom)));
}

const alertPixels = [
  ".###.",
  "#YYY#",
  "#YYY#",
  "#YYY#",
  "#YYY#",
  "#YYY#",
  "#####",
  "#YYY#",
  "#YYY#",
  ".###.",
];
const alertColors: Record<string, string> = {
  "#": "#2e2b27",
  Y: "#ffe25c",
};
const alertDurationMs = 750;

let alertImage = "";

// Pops a pixel-art "!" for a pet that has just noticed something. left and
// bottom are the mark's bottom-centre; ownerBottom keeps it at the pet's depth.
export function showPetAlert(
  left: number,
  bottom: number,
  ownerBottom: number,
  scale: number,
): void {
  const element = document.createElement("div");
  element.className = "pet-alert";
  element.style.width = `${alertPixels[0].length * scale}px`;
  element.style.height = `${alertPixels.length * scale}px`;
  element.style.left = `${Math.round(left)}px`;
  element.style.bottom = `${Math.round(bottom)}px`;
  element.style.zIndex = getPetDepth(ownerBottom);
  element.style.backgroundImage = `url("${getAlertImage()}")`;
  element.style.animationDuration = `${alertDurationMs}ms`;

  getPetLayer().append(element);
  window.setTimeout(() => element.remove(), alertDurationMs);
}

function getAlertImage(): string {
  if (alertImage) {
    return alertImage;
  }

  const canvas = document.createElement("canvas");
  canvas.width = alertPixels[0].length;
  canvas.height = alertPixels.length;
  const context = canvas.getContext("2d");
  if (!context) {
    return "";
  }

  alertPixels.forEach((row, y) => {
    [...row].forEach((pixel, x) => {
      const color = alertColors[pixel];
      if (color) {
        context.fillStyle = color;
        context.fillRect(x, y, 1, 1);
      }
    });
  });

  alertImage = canvas.toDataURL();
  return alertImage;
}
